import { useCallback, useEffect, useState, type ReactNode } from "react";
import { dataLonga, tempoRelativo, useT } from "../i18n";
import { useErroEmToast } from "../ui/avisos";
import {
  FilaRanqueadaSchema,
  MeuRanqueadoSchema,
  PaginaDoLeaderboardSchema,
  TemporadaRanqueadaSchema,
  type FilaRanqueada,
  type LinhaDoLeaderboard,
  type MeuRanqueado,
  type TemporadaRanqueada,
} from "../online/esquemas";
import { ErroDaApi } from "../online/cliente";
import { mensagemDoErro, useSessao } from "../online/sessao";
import { Panel, Stat } from "../ui/atoms";
import { Pagina } from "../ui/Pagina";
import { TituloDeTreinador } from "../ui/cosmeticos";

/**
 * Ranqueado: fila, temporada em curso, leaderboard e histórico.
 *
 * O modo é uma temporada curta contra gente de MMR parecido, com uma rodada por
 * dia, e o desenho da tela segue três coisas que o servidor decide e o cliente
 * só mostra.
 *
 * O **MMR muda pela posição final**, nunca por partida. Por isso o histórico
 * mostra a temporada inteira em uma linha, com o MMR de entrada e o de saída, e
 * não uma sequência de duelos que não existe.
 *
 * A **fila é tabela no banco**, não uma sala aberta enquanto o jogo estiver na
 * tela. Sair do jogo não tira ninguém dela, e é por isso que a tela diz desde
 * quando você está esperando e oferece a saída explícita.
 *
 * A **fila é do Passe**. O servidor confere o direito no banco a cada entrada e
 * responde `ENTITLEMENT_REQUIRED` a quem não tem. A tela não esconde o modo por
 * causa disso: temporada, divisões e leaderboard são leitura livre para quem
 * está pensando em entrar, e o recado sobre o Passe aparece no lugar onde ele
 * importa, que é o botão da fila.
 */

const POR_PAGINA = 25;

/** Região da fila. Duas letras minúsculas, como o servidor exige. */
const REGIAO = "br";

/**
 * Nome da divisão pelo número gravado.
 *
 * A entrada e o histórico guardam o número, não o nome, porque a lista de
 * divisões pode ser renomeada entre temporadas. Quem traduz é a temporada que
 * está na mão, e o número cru fica de plano de queda: divisão desconhecida
 * aparece como número em vez de sumir da linha.
 */
function nomeDaDivisao(numero: number, temporada: TemporadaRanqueada | null): string {
  return temporada?.divisions.find((qual) => qual.division === numero)?.name ?? String(numero);
}

export function Ranqueado({
  aoAbrirLiga,
  aoAbrirLoja,
  aoVoltar,
}: {
  /** Chamado quando a liga ranqueada da pessoa já está montada. */
  aoAbrirLiga?: (leagueId: string) => void;
  /** Chamado quando falta o Passe e a pessoa quer resolver isso. */
  aoAbrirLoja?: () => void;
  aoVoltar?: () => void;
}): ReactNode {
  const t = useT();
  const sessao = useSessao();

  const [temporada, definirTemporada] = useState<TemporadaRanqueada | null>(null);
  const [meu, definirMeu] = useState<MeuRanqueado | null>(null);
  const [erro, definirErro] = useState<string | null>(null);
  useErroEmToast(erro);

  const carregar = useCallback(async () => {
    try {
      const [novaTemporada, novoMeu] = await Promise.all([
        sessao.cliente.pedir(
          { metodo: "GET", caminho: "/v1/ranked/season" },
          TemporadaRanqueadaSchema,
        ),
        sessao.cliente.pedir({ metodo: "GET", caminho: "/v1/ranked/me" }, MeuRanqueadoSchema),
      ]);
      definirTemporada(novaTemporada);
      definirMeu(novoMeu);
      definirErro(null);
    } catch (causa) {
      definirErro(mensagemDoErro(causa));
    }
  }, [sessao]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  return (
    <Pagina titulo={t("ranqueado.titulo")} aoVoltar={aoVoltar}>

      {temporada === null && meu === null && !erro && (
        <p className="muted" style={{ textAlign: "center" }}>
          {t("online.carregando")}
        </p>
      )}

      {temporada && <Temporada temporada={temporada} meu={meu} />}

      <Fila
        meu={meu}
        aoMudar={() => void carregar()}
        aoAbrirLiga={aoAbrirLiga}
        aoAbrirLoja={aoAbrirLoja}
      />

      {meu && <EmDisputa meu={meu} temporada={temporada} aoAbrirLiga={aoAbrirLiga} />}

      <Leaderboard temporada={temporada} />

      {meu && <Historico meu={meu} temporada={temporada} />}
    </Pagina>
  );
}

// ---------------------------------------------------------------------------
// Temporada
// ---------------------------------------------------------------------------

function Temporada({
  temporada,
  meu,
}: {
  temporada: TemporadaRanqueada;
  meu: MeuRanqueado | null;
}): ReactNode {
  const t = useT();

  return (
    <Panel title={t("ranqueado.temporada", { numero: temporada.number })}>
      <div
        className="grid"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}
      >
        <Stat label={t("ranqueado.temporadaSituacao")}>
          {t(`ranqueado.temporadaEstados.${temporada.status}`)}
        </Stat>
        <Stat label={t("ranqueado.meuMmr")}>
          <span className="num">{meu?.mmr ?? "-"}</span>
        </Stat>
        <Stat label={t("ranqueado.minhaDivisao")}>{meu?.division.name ?? "-"}</Stat>
      </div>

      <p className="muted" style={{ margin: "10px 0 0", fontSize: 11.5 }}>
        {t("ranqueado.temporadaPeriodo", {
          inicio: dataLonga(temporada.startsAt),
          fim: dataLonga(temporada.endsAt),
        })}
        {" · "}
        {t("ranqueado.temporadaSemanas", { semanas: temporada.weeks })}
        {" · "}
        {t("ranqueado.temporadaTreinadores", { total: temporada.managersPerLeague })}
      </p>

      <div style={{ marginTop: 12 }}>
        <span className="label">{t("ranqueado.divisoes")}</span>
        <div className="row" style={{ gap: 8, flexWrap: "wrap", marginTop: 6 }}>
          {temporada.divisions.map((divisao) => {
            const minha = meu?.division.division === divisao.division;
            return (
              <span
                key={divisao.division}
                title={t("ranqueado.divisaoFaixa", { mmr: divisao.minMmr })}
                style={{
                  padding: "3px 10px",
                  borderRadius: "var(--radius-sm)",
                  fontSize: 11.5,
                  border: `1px solid ${minha ? "var(--primary)" : "var(--border)"}`,
                  color: minha ? "var(--primary)" : "var(--text-muted)",
                  fontWeight: minha ? 700 : 400,
                }}
              >
                {divisao.name}
              </span>
            );
          })}
        </div>
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Fila
// ---------------------------------------------------------------------------

function Fila({
  meu,
  aoMudar,
  aoAbrirLiga,
  aoAbrirLoja,
}: {
  meu: MeuRanqueado | null;
  aoMudar: () => void;
  aoAbrirLiga: ((leagueId: string) => void) | undefined;
  aoAbrirLoja: (() => void) | undefined;
}): ReactNode {
  const t = useT();
  const sessao = useSessao();
  const [ocupado, definirOcupado] = useState<"entrar" | "sair" | null>(null);
  const [resultado, definirResultado] = useState<FilaRanqueada | null>(null);
  const [aviso, definirAviso] = useState<string | null>(null);
  const [erro, definirErro] = useState<string | null>(null);
  useErroEmToast(erro);
  /** O Passe faltando não é erro de uso, e não vai para a faixa vermelha. */
  const [faltaPasse, definirFaltaPasse] = useState(false);

  // A resposta da entrada é mais fresca que o perfil lido antes dela, e é ela
  // que sabe posição e quantos faltam. O perfil cobre quem chegou já na fila.
  const naFila = resultado?.queued ?? meu?.queue !== null;
  const desdeQuando = resultado?.enqueuedAt ?? meu?.queue?.enqueuedAt ?? null;
  const ligaPronta = resultado?.leagueId ?? meu?.entry?.leagueId ?? null;

  const entrar = async (): Promise<void> => {
    definirErro(null);
    definirAviso(null);
    definirFaltaPasse(false);
    definirOcupado("entrar");
    try {
      const nova = await sessao.cliente.pedir(
        { metodo: "POST", caminho: "/v1/ranked/queue", corpo: { region: REGIAO } },
        FilaRanqueadaSchema,
      );
      definirResultado(nova);
      aoMudar();
    } catch (causa) {
      if (causa instanceof ErroDaApi && causa.code === "ENTITLEMENT_REQUIRED") {
        definirFaltaPasse(true);
        return;
      }
      definirErro(mensagemDoErro(causa));
    } finally {
      definirOcupado(null);
    }
  };

  const sair = async (): Promise<void> => {
    definirErro(null);
    definirAviso(null);
    definirOcupado("sair");
    try {
      // Corpo vazio explícito: o cliente da API anuncia JSON em todo pedido, e
      // um pedido que anuncia JSON sem mandar nada faz este servidor responder
      // 500. O mesmo cuidado vale para a loja e para a liberação do comissário.
      await sessao.cliente.pedirVazio({ metodo: "DELETE", caminho: "/v1/ranked/queue", corpo: {} });
      definirResultado(null);
      definirAviso(t("ranqueado.saiuDaFila"));
      aoMudar();
    } catch (causa) {
      definirErro(mensagemDoErro(causa));
    } finally {
      definirOcupado(null);
    }
  };

  return (
    <Panel title={t("ranqueado.fila")}>
      <p className="muted" style={{ margin: 0, fontSize: 11.5 }}>
        {t("ranqueado.filaAjuda")}
      </p>

      <p style={{ margin: "10px 0 0", fontSize: 12.5 }}>
        {naFila && desdeQuando
          ? t("ranqueado.naFila", { quando: tempoRelativo(Date.parse(desdeQuando)) })
          : t("ranqueado.foraDaFila")}
      </p>

      {resultado?.queued && resultado.position !== null && resultado.region !== null && (
        <p className="muted" style={{ margin: "4px 0 0", fontSize: 11.5 }}>
          {t("ranqueado.naFilaPosicao", {
            posicao: resultado.position,
            total: resultado.waiting,
            regiao: resultado.region.toUpperCase(),
          })}
        </p>
      )}
      {resultado?.queued && resultado.needed !== undefined && (
        <p className="muted" style={{ margin: "4px 0 0", fontSize: 11.5 }}>
          {resultado.needed > 0
            ? t("ranqueado.naFilaFaltam", { quantos: resultado.needed })
            : t("ranqueado.naFilaCompleta")}
        </p>
      )}

      {ligaPronta && (
        <div className="row" style={{ gap: 8, flexWrap: "wrap", marginTop: 10 }}>
          <span style={{ color: "var(--success)", fontSize: 12.5, fontWeight: 700 }}>
            {t("ranqueado.ligaPronta")}
          </span>
          {aoAbrirLiga && (
            <button className="primary" onClick={() => aoAbrirLiga(ligaPronta)}>
              {t("ranqueado.abrirLiga")}
            </button>
          )}
        </div>
      )}

      {faltaPasse && (
        <div
          role="status"
          style={{
            marginTop: 10,
            padding: "10px 12px",
            border: "1px solid var(--warning)",
            borderRadius: "var(--radius-sm)",
            background: "var(--surface-2)",
          }}
        >
          <p style={{ margin: 0, fontSize: 12.5 }}>{t("ranqueado.exigePasse")}</p>
          {aoAbrirLoja && (
            <button className="ghost" onClick={aoAbrirLoja} style={{ marginTop: 8 }}>
              {t("ranqueado.irParaLoja")}
            </button>
          )}
        </div>
      )}

      {aviso && (
        <p role="status" style={{ margin: "8px 0 0", color: "var(--primary)", fontSize: 12.5 }}>
          {aviso}
        </p>
      )}

      <div className="row" style={{ gap: 8, flexWrap: "wrap", marginTop: 12 }}>
        <button
          className="primary"
          disabled={ocupado !== null || naFila || ligaPronta !== null}
          onClick={() => void entrar()}
        >
          {ocupado === "entrar" ? t("ranqueado.entrando") : t("ranqueado.entrarNaFila")}
        </button>
        <button className="ghost" disabled={ocupado !== null || !naFila} onClick={() => void sair()}>
          {ocupado === "sair" ? t("ranqueado.saindo") : t("ranqueado.sairDaFila")}
        </button>
        <span className="label-muted" style={{ marginLeft: "auto" }}>
          {t("ranqueado.regiao")}: {REGIAO.toUpperCase()}
        </span>
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Temporada em curso
// ---------------------------------------------------------------------------

function EmDisputa({
  meu,
  temporada,
  aoAbrirLiga,
}: {
  meu: MeuRanqueado;
  temporada: TemporadaRanqueada | null;
  aoAbrirLiga: ((leagueId: string) => void) | undefined;
}): ReactNode {
  const t = useT();
  const entrada = meu.entry;

  return (
    <Panel title={t("ranqueado.emDisputa")}>
      {!entrada && (
        <p className="muted" style={{ margin: 0 }}>
          {t("ranqueado.semEntrada")}
        </p>
      )}

      {entrada && (
        <>
          <p style={{ margin: 0, fontSize: 12.5 }}>
            {t("ranqueado.minhaEntrada", {
              divisao: nomeDaDivisao(entrada.division, temporada),
              mmr: entrada.mmrStart,
            })}
          </p>
          <p className="muted" style={{ margin: "4px 0 0", fontSize: 11.5 }}>
            {entrada.position === null
              ? t("ranqueado.semPosicaoAinda")
              : t("ranqueado.minhaPosicao", { posicao: entrada.position })}
          </p>
          {aoAbrirLiga && (
            <button
              className="primary"
              onClick={() => aoAbrirLiga(entrada.leagueId)}
              style={{ marginTop: 10 }}
            >
              {t("ranqueado.abrirLiga")}
            </button>
          )}
        </>
      )}
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Leaderboard
// ---------------------------------------------------------------------------

/**
 * Tabela de MMR, paginada por cursor.
 *
 * O cursor é opaco e a ordem é do servidor (MMR decrescente), então a posição
 * mostrada é a contagem da lista acumulada, e não um número que veio de lá.
 * Filtrar por divisão reinicia a paginação de propósito: um cursor do conjunto
 * anterior apontaria para uma linha que o filtro novo nem tem.
 */
function Leaderboard({ temporada }: { temporada: TemporadaRanqueada | null }): ReactNode {
  const t = useT();
  const sessao = useSessao();
  const [divisao, definirDivisao] = useState<number | null>(null);
  const [itens, definirItens] = useState<LinhaDoLeaderboard[] | null>(null);
  const [cursor, definirCursor] = useState<string | null>(null);
  const [erro, definirErro] = useState<string | null>(null);
  useErroEmToast(erro);
  const [ocupado, definirOcupado] = useState(false);

  const carregar = useCallback(
    async (apartirDe: string | null) => {
      definirOcupado(true);
      definirErro(null);
      try {
        const busca = new URLSearchParams({ limit: String(POR_PAGINA) });
        if (divisao !== null) busca.set("division", String(divisao));
        if (apartirDe) busca.set("cursor", apartirDe);
        const pagina = await sessao.cliente.pedir(
          { metodo: "GET", caminho: `/v1/ranked/leaderboard?${busca.toString()}` },
          PaginaDoLeaderboardSchema,
        );
        definirItens((atuais) => (apartirDe ? [...(atuais ?? []), ...pagina.items] : pagina.items));
        definirCursor(pagina.nextCursor);
      } catch (causa) {
        definirErro(mensagemDoErro(causa));
        definirItens((atuais) => atuais ?? []);
      } finally {
        definirOcupado(false);
      }
    },
    [sessao, divisao],
  );

  useEffect(() => {
    void carregar(null);
  }, [carregar]);

  const eu = sessao.conta?.usuario.id ?? null;

  return (
    <Panel
      title={t("ranqueado.leaderboard")}
      action={
        <button
          className="ghost"
          disabled={ocupado}
          onClick={() => void carregar(null)}
          style={{ minHeight: 26, padding: "2px 8px", fontSize: 11 }}
        >
          {t("ranqueado.atualizar")}
        </button>
      }
    >
      <div className="row" style={{ gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
        <button
          className="ghost"
          onClick={() => definirDivisao(null)}
          style={{
            minHeight: 28,
            padding: "2px 10px",
            fontSize: 11.5,
            ...(divisao === null
              ? { borderColor: "var(--primary)", color: "var(--primary)", fontWeight: 700 }
              : {}),
          }}
        >
          {t("ranqueado.leaderboardTodas")}
        </button>
        {(temporada?.divisions ?? []).map((entrada) => (
          <button
            key={entrada.division}
            className="ghost"
            onClick={() => definirDivisao(entrada.division)}
            style={{
              minHeight: 28,
              padding: "2px 10px",
              fontSize: 11.5,
              ...(divisao === entrada.division
                ? { borderColor: "var(--primary)", color: "var(--primary)", fontWeight: 700 }
                : {}),
            }}
          >
            {entrada.name}
          </button>
        ))}
      </div>

      {itens === null && (
        <p className="muted" style={{ margin: 0 }}>
          {t("online.carregando")}
        </p>
      )}
      {itens !== null && itens.length === 0 && (
        <p className="muted" style={{ margin: 0 }}>
          {t("ranqueado.semLeaderboard")}
        </p>
      )}

      {itens !== null && itens.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
          <thead>
            <tr>
              <th className="label-muted" style={{ textAlign: "right", width: 34 }}>
                {t("ranqueado.colunaPosicao")}
              </th>
              <th className="label-muted" style={{ textAlign: "left" }}>
                {t("ranqueado.colunaTreinador")}
              </th>
              <th className="label-muted" style={{ textAlign: "left" }}>
                {t("ranqueado.colunaDivisao")}
              </th>
              <th className="label-muted" style={{ textAlign: "right" }}>
                {t("ranqueado.colunaMmr")}
              </th>
              <th className="label-muted" style={{ textAlign: "right" }}>
                {t("ranqueado.colunaNaLiga")}
              </th>
            </tr>
          </thead>
          <tbody>
            {itens.map((linha, indice) => (
              <tr
                key={linha.userId}
                style={linha.userId === eu ? { color: "var(--primary)", fontWeight: 700 } : undefined}
              >
                <td className="num" style={{ textAlign: "right", padding: "4px 0" }}>
                  {indice + 1}
                </td>
                <td style={{ padding: "4px 8px" }}>
                  {linha.displayName}
                  {/*
                    O título comprado aparece aqui porque o leaderboard é onde
                    ele vale: cosmético que ninguém vê não é cosmético. Vem do
                    servidor já conferido contra o direito, então quem deixou de
                    ter some da lista sem ninguém precisar limpar nada.
                  */}
                  {linha.title && <TituloDeTreinador params={linha.title} />}
                  {linha.userId === eu && (
                    <span className="label-muted"> ({t("ranqueado.voce")})</span>
                  )}
                </td>
                <td className="muted" style={{ padding: "4px 8px", fontSize: 11.5 }}>
                  {nomeDaDivisao(linha.division, temporada)}
                </td>
                <td className="num" style={{ textAlign: "right", padding: "4px 0" }}>
                  {linha.mmr}
                </td>
                <td className="num" style={{ textAlign: "right", padding: "4px 0" }}>
                  {linha.position ?? "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {cursor && (
        <button
          className="ghost"
          disabled={ocupado}
          onClick={() => void carregar(cursor)}
          style={{ marginTop: 10 }}
        >
          {t("ranqueado.carregarMais")}
        </button>
      )}
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Histórico
// ---------------------------------------------------------------------------

function Historico({
  meu,
  temporada,
}: {
  meu: MeuRanqueado;
  temporada: TemporadaRanqueada | null;
}): ReactNode {
  const t = useT();

  return (
    <Panel title={t("ranqueado.historico")}>
      {meu.history.length === 0 && (
        <p className="muted" style={{ margin: 0 }}>
          {t("ranqueado.semHistorico")}
        </p>
      )}

      <div className="grid" style={{ gap: 10 }}>
        {meu.history.map((linha) => (
          <div key={`${linha.season}:${linha.leagueId}`} className="row" style={{ gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600 }}>
                {t("ranqueado.historicoLinha", {
                  numero: linha.season,
                  divisao: nomeDaDivisao(linha.division, temporada),
                })}
              </div>
              <div className="muted" style={{ fontSize: 11.5 }}>
                {linha.mmrEnd === null
                  ? t("ranqueado.historicoSemFim", { inicio: linha.mmrStart })
                  : t("ranqueado.historicoVariacao", {
                      inicio: linha.mmrStart,
                      fim: linha.mmrEnd,
                    })}
                {linha.position !== null &&
                  ` · ${t("ranqueado.historicoPosicao", { posicao: linha.position })}`}
              </div>
              <div className="muted" style={{ fontSize: 11 }}>
                {t("ranqueado.temporadaPeriodo", {
                  inicio: dataLonga(linha.startsAt),
                  fim: dataLonga(linha.endsAt),
                })}
              </div>
            </div>
            <span className="label-muted">{t(`ranqueado.temporadaEstados.${linha.status}`)}</span>
          </div>
        ))}
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Peças de tela
// ---------------------------------------------------------------------------

